/**
 * LayerStyleDialog — full Photoshop-style Layer Style dialog.
 *
 * Extracted from main.ts so the 11 k-line file stays manageable.
 *
 * Deps injected via LayerStyleDialogDeps so the module is unit-testable
 * without importing half of main.ts.
 */

import type {
  LayerStyles,
  RGBA,
  ContourSpec,
} from "../../core/document/LayerStyle";
import type { RGB } from "./ColorPicker";
import { trapFocus } from "./ColorPicker";

// ── helpers ──────────────────────────────────────────────────────────────────

function rgbaToRgb(c: RGBA): RGB {
  return { r: c.r, g: c.g, b: c.b };
}
function rgbToRgba(c: RGB, a = 255): RGBA {
  return { r: c.r, g: c.g, b: c.b, a };
}
function rgbToHex(c: RGB): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`;
}
function hexToRgb(hex: string): RGB | null {
  const s = hex.replace("#", "");
  if (s.length !== 6) return null;
  const n = parseInt(s, 16);
  if (isNaN(n)) return null;
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

// Preset contour specs shipped with Photoshop.
const CONTOUR_PRESETS: ReadonlyArray<{ name: string; spec: ContourSpec }> = [
  { name: "Linear",   spec: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }],              presetName: "Linear" } },
  { name: "Gaussian", spec: { points: [{ x: 0, y: 0 }, { x: 0.5, y: 0.5 }, { x: 1, y: 1 }], presetName: "Gaussian" } },
  { name: "Ring",     spec: { points: [{ x: 0, y: 1 }, { x: 0.5, y: 0 }, { x: 1, y: 1 }], presetName: "Ring" } },
  { name: "Cove",     spec: { points: [{ x: 0, y: 1 }, { x: 1, y: 0 }],              presetName: "Cove" } },
];

const BLEND_MODES = [
  "normal", "multiply", "screen", "overlay", "darken", "lighten",
  "color-dodge", "color-burn", "hard-light", "soft-light",
  "difference", "exclusion",
] as const;

// ── public deps interface ─────────────────────────────────────────────────────

export interface LayerStyleDialogDeps {
  /** Opens the app color picker, resolves with the chosen color or null. */
  openColorPicker(initial: RGB): Promise<RGB | null>;
  /** Current global light angle (degrees). */
  globalLightAngle: number;
  /** Callback when the user changes Use Global Light → updates module variable. */
  onGlobalLightAngleChange(angle: number): void;
}

// ── main export ───────────────────────────────────────────────────────────────

/**
 * Open the Layer Style dialog.
 *
 * @param initialStyles  The layer's current styles object (plain object, mutable copy ok).
 * @param deps           Injected dependencies (color picker, global light).
 * @returns  The updated styles on OK, null on cancel.
 */
export async function openLayerStyleDialog(
  initialStyles: LayerStyles,
  deps: LayerStyleDialogDeps,
): Promise<LayerStyles | null> {
  // Work on a mutable deep-ish clone so the caller's object isn't mutated
  // until the user clicks OK.
  const styles: Record<string, any> = JSON.parse(JSON.stringify(initialStyles));

  return new Promise<LayerStyles | null>((resolve) => {
    // Remember the element that had focus so we can restore it on close (#213).
    const openerEl = document.activeElement as HTMLElement | null;

    // ── DOM scaffold ─────────────────────────────────────────────────────────

    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;top:0;left:0;right:0;bottom:0;" +
      "background:rgba(0,0,0,0.6);display:flex;align-items:center;" +
      "justify-content:center;z-index:10000;";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "lsd-title");

    const dialog = document.createElement("div");
    dialog.style.cssText =
      "background:#2d2d2d;border:1px solid #555;border-radius:2px;" +
      "width:700px;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;";

    dialog.innerHTML = `
      <div style="padding:8px 12px;border-bottom:1px solid #444;display:flex;align-items:center;justify-content:space-between;">
        <h3 id="lsd-title" style="margin:0;color:#fff;font-size:11px;font-weight:600;">Layer Style</h3>
        <button data-action="close" aria-label="Close" style="background:none;border:none;color:#aaa;cursor:pointer;width:16px;height:16px;display:flex;align-items:center;justify-content:center;padding:0;"><svg width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg></button>
      </div>
      <div style="display:flex;flex:1;overflow:hidden;">
        <div id="lsd-effects" style="width:180px;border-right:1px solid #444;padding:8px;overflow-y:auto;background:#252525;"></div>
        <div id="lsd-params" style="flex:1;padding:16px;overflow-y:auto;"></div>
      </div>
      <div style="padding:8px 12px;border-top:1px solid #444;display:flex;justify-content:flex-end;gap:8px;">
        <button data-action="cancel" style="padding:4px 12px;background:#555;border:none;color:#fff;border-radius:2px;cursor:pointer;font-size:11px;">Cancel</button>
        <button data-action="ok" style="padding:4px 12px;background:var(--accent);border:none;color:#fff;border-radius:2px;cursor:pointer;font-size:11px;">OK</button>
      </div>
    `;

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    const effectsList = dialog.querySelector<HTMLElement>("#lsd-effects")!;
    const paramsPanel = dialog.querySelector<HTMLElement>("#lsd-params")!;

    // ── Effects list (left sidebar) ──────────────────────────────────────────

    const EFFECTS: Array<{ id: string; label: string }> = [
      { id: "dropShadow",      label: "Drop Shadow" },
      { id: "innerShadow",     label: "Inner Shadow" },
      { id: "outerGlow",       label: "Outer Glow" },
      { id: "innerGlow",       label: "Inner Glow" },
      { id: "bevelEmboss",     label: "Bevel & Emboss" },
      { id: "satin",           label: "Satin" },
      { id: "colorOverlay",    label: "Color Overlay" },
      { id: "gradientOverlay", label: "Gradient Overlay" },
      { id: "patternOverlay",  label: "Pattern Overlay" },
      { id: "stroke",          label: "Stroke" },
    ];

    for (const eff of EFFECTS) {
      const row = document.createElement("label");
      row.style.cssText =
        "display:flex;align-items:center;gap:6px;color:#ccc;font-size:12px;" +
        "cursor:pointer;padding:4px 0;user-select:none;";
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.dataset.effect = eff.id;
      cb.checked = !!(styles[eff.id]?.enabled);

      cb.addEventListener("change", () => {
        if (!styles[eff.id]) styles[eff.id] = { enabled: false };
        styles[eff.id].enabled = cb.checked;
      });

      const nameSpan = document.createElement("span");
      nameSpan.textContent = eff.label;
      nameSpan.style.cursor = "pointer";
      nameSpan.addEventListener("click", (e) => {
        e.preventDefault();
        showParams(eff.id);
      });

      row.appendChild(cb);
      row.appendChild(nameSpan);
      effectsList.appendChild(row);
    }

    // ── Parameter panel renderer ─────────────────────────────────────────────

    let currentEffect = "dropShadow";

    function colorSwatch(colorVal: RGBA | undefined, paramKey: string): string {
      const c = colorVal ?? { r: 0, g: 0, b: 0, a: 255 };
      const hex = rgbToHex(c);
      return `<button
        data-color-swatch="${paramKey}"
        title="Click to pick color"
        style="
          width:32px;height:20px;background:${hex};
          border:1px solid #888;border-radius:2px;cursor:pointer;
          vertical-align:middle;flex-shrink:0;
        "
        aria-label="Pick color for ${paramKey}"></button>`;
    }

    function contourThumbnails(currentPresetName: string | undefined): string {
      let html = `<div style="display:flex;gap:4px;flex-wrap:wrap;margin-top:4px;">`;
      for (const preset of CONTOUR_PRESETS) {
        const selected = (currentPresetName ?? "Linear") === preset.name;
        html += `<button
          data-contour-preset="${preset.name}"
          title="${preset.name}"
          style="
            width:40px;height:40px;padding:2px;
            background:${selected ? "var(--accent)" : "#333"};
            border:1px solid ${selected ? "var(--accent-light,#60b0ff)" : "#555"};
            border-radius:1px;cursor:pointer;
          ">
          <svg viewBox="0 0 32 32" width="34" height="34" xmlns="http://www.w3.org/2000/svg">
            ${contourPath(preset.spec)}
          </svg>
        </button>`;
      }
      html += `</div>`;
      return html;
    }

    function contourPath(spec: ContourSpec): string {
      const pts = spec.points;
      if (pts.length === 0) return "";
      const scale = (v: number, lo = 0, hi = 32) => lo + v * (hi - lo);
      const toSvg = (p: { x: number; y: number }) =>
        `${scale(p.x).toFixed(1)},${scale(1 - p.y).toFixed(1)}`;
      let d = `M ${toSvg(pts[0]!)}`;
      for (let i = 1; i < pts.length; i++) d += ` L ${toSvg(pts[i]!)}`;
      return `<path d="${d}" stroke="#aef" stroke-width="2" fill="none"/>`;
    }

    function showParams(effectId: string): void {
      currentEffect = effectId;
      const eff = styles[effectId] ?? {};
      paramsPanel.innerHTML = "";

      const title = document.createElement("h4");
      title.style.cssText = "margin:0 0 12px;color:#fff;font-size:11px;font-weight:600;";
      title.textContent = EFFECTS.find((e) => e.id === effectId)?.label ?? effectId;
      paramsPanel.appendChild(title);

      // ── per-effect controls ──────────────────────────────────────────────

      if (effectId === "dropShadow" || effectId === "innerShadow") {
        addColorRow("color", "Color", eff.color ?? { r: 0, g: 0, b: 0, a: 255 });
        addBlendModeRow("blendMode", "Blend Mode", eff.blendMode ?? "multiply");
        addSliderRow("opacity", "Opacity", eff.opacity != null ? Math.round(eff.opacity * 100) : 75, 0, 100, "%");
        addGlobalLightRow("angle", "Angle", eff.angle ?? deps.globalLightAngle, eff.useGlobalLight ?? false);
        addSliderRow("distance", "Distance", eff.distance ?? 5, 0, 250, "px");
        if (effectId === "dropShadow") {
          addSliderRow("spread", "Spread", eff.spread ?? 0, 0, 100, "%");
        } else {
          addSliderRow("choke", "Choke", eff.choke ?? 0, 0, 100, "%");
        }
        addSliderRow("size", "Size", eff.size ?? 5, 0, 250, "px");
        addSliderRow("noise", "Noise", eff.noise != null ? Math.round(eff.noise * 100) : 0, 0, 100, "%");
      }

      if (effectId === "outerGlow" || effectId === "innerGlow") {
        // Fill: color or gradient.  For simplicity show a color swatch (flat color mode).
        const fillColor: RGBA = eff.fill?.color ?? (effectId === "outerGlow" ? { r: 255, g: 200, b: 50, a: 255 } : { r: 255, g: 255, b: 255, a: 255 });
        addColorRow("fill.color", "Color", fillColor);
        addBlendModeRow("blendMode", "Blend Mode", eff.blendMode ?? "screen");
        addSliderRow("opacity", "Opacity", eff.opacity != null ? Math.round(eff.opacity * 100) : 75, 0, 100, "%");
        addSliderRow("noise", "Noise", eff.noise != null ? Math.round(eff.noise * 100) : 0, 0, 100, "%");
        addDropdownRow("technique", "Technique", eff.technique ?? "softer",
          [{ value: "softer", label: "Softer" }, { value: "precise", label: "Precise" }]);
        addSliderRow("spread", "Spread", eff.spread != null ? Math.round(eff.spread * 100) : 0, 0, 100, "%");
        addSliderRow("size", "Size", eff.size ?? 5, 0, 250, "px");
        if (effectId === "innerGlow") {
          addDropdownRow("source", "Source", eff.source ?? "edge",
            [{ value: "edge", label: "Edge" }, { value: "center", label: "Center" }]);
          addSliderRow("choke", "Choke", eff.choke != null ? Math.round(eff.choke * 100) : 0, 0, 100, "%");
        }
        addSliderRow("range", "Range", eff.range != null ? Math.round(eff.range * 100) : 50, 0, 100, "%");
        addSliderRow("jitter", "Jitter", eff.jitter != null ? Math.round(eff.jitter * 100) : 0, 0, 100, "%");
      }

      if (effectId === "bevelEmboss") {
        addDropdownRow("style", "Style", eff.style ?? "inner-bevel",
          ["outer-bevel", "inner-bevel", "emboss", "pillow-emboss", "stroke-emboss"]
            .map((v) => ({ value: v, label: v.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) })));
        addDropdownRow("technique", "Technique", eff.technique ?? "smooth",
          [{ value: "smooth", label: "Smooth" }, { value: "chisel-hard", label: "Chisel Hard" }, { value: "chisel-soft", label: "Chisel Soft" }]);
        addSliderRow("depth", "Depth", eff.depth != null ? Math.round(eff.depth * 100) : 100, 1, 1000, "%");
        addDropdownRow("direction", "Direction", eff.direction ?? "up",
          [{ value: "up", label: "Up" }, { value: "down", label: "Down" }]);
        addSliderRow("size", "Size", eff.size ?? 5, 0, 250, "px");
        addSliderRow("soften", "Soften", eff.soften ?? 0, 0, 16, "px");
        addGlobalLightRow("angle", "Angle", eff.angle ?? deps.globalLightAngle, eff.useGlobalLight ?? false);
        addSliderRow("altitude", "Altitude", eff.altitude ?? 30, 0, 90, "°");

        // Gloss Contour
        addSectionLabel("Gloss Contour");
        addContourRow("glossContour", eff.glossContour);

        // Highlight / Shadow
        addSectionLabel("Shading");
        addBlendModeRow("highlightBlendMode", "Highlight Mode", eff.highlightBlendMode ?? "screen");
        addColorRow("highlightColor", "Highlight Color", eff.highlightColor ?? { r: 255, g: 255, b: 255, a: 255 });
        addSliderRow("highlightOpacity", "Highlight Opacity", eff.highlightOpacity != null ? Math.round(eff.highlightOpacity * 100) : 75, 0, 100, "%");
        addBlendModeRow("shadowBlendMode", "Shadow Mode", eff.shadowBlendMode ?? "multiply");
        addColorRow("shadowColor", "Shadow Color", eff.shadowColor ?? { r: 0, g: 0, b: 0, a: 255 });
        addSliderRow("shadowOpacity", "Shadow Opacity", eff.shadowOpacity != null ? Math.round(eff.shadowOpacity * 100) : 75, 0, 100, "%");
      }

      if (effectId === "satin") {
        addColorRow("color", "Color", eff.color ?? { r: 0, g: 0, b: 0, a: 255 });
        addBlendModeRow("blendMode", "Blend Mode", eff.blendMode ?? "multiply");
        addSliderRow("opacity", "Opacity", eff.opacity != null ? Math.round(eff.opacity * 100) : 50, 0, 100, "%");
        addSliderRow("angle", "Angle", eff.angle ?? 19, 0, 360, "°");
        addSliderRow("distance", "Distance", eff.distance ?? 11, 0, 250, "px");
        addSliderRow("size", "Size", eff.size ?? 14, 0, 250, "px");
        addCheckboxRow("invert", "Invert", !!eff.invert);
      }

      if (effectId === "colorOverlay") {
        addColorRow("color", "Color", eff.color ?? { r: 255, g: 0, b: 0, a: 255 });
        addBlendModeRow("blendMode", "Blend Mode", eff.blendMode ?? "normal");
        addSliderRow("opacity", "Opacity", eff.opacity != null ? Math.round(eff.opacity * 100) : 100, 0, 100, "%");
      }

      if (effectId === "gradientOverlay") {
        // Gradient stops: show first color stop swatch for now.
        const stops = eff.gradient?.colorStops;
        const firstStop: RGBA = stops?.[0]?.color ?? { r: 0, g: 0, b: 0, a: 255 };
        const lastStop: RGBA = stops?.[stops.length - 1]?.color ?? { r: 255, g: 255, b: 255, a: 255 };
        addColorRow("gradient.stop0.color", "Start Color", firstStop);
        addColorRow("gradient.stop1.color", "End Color", lastStop);
        addBlendModeRow("blendMode", "Blend Mode", eff.blendMode ?? "normal");
        addSliderRow("opacity", "Opacity", eff.opacity != null ? Math.round(eff.opacity * 100) : 100, 0, 100, "%");
        addDropdownRow("style", "Style", eff.style ?? "linear",
          ["linear", "radial", "angle", "reflected", "diamond"]
            .map((v) => ({ value: v, label: (v[0] ?? "").toUpperCase() + v.slice(1) })));
        addSliderRow("angle", "Angle", eff.angle ?? 90, 0, 360, "°");
        addSliderRow("scale", "Scale", eff.scale != null ? Math.round(eff.scale * 100) : 100, 10, 150, "%");
        addCheckboxRow("reverse", "Reverse", !!eff.reverse);
        addCheckboxRow("alignWithLayer", "Align with Layer", eff.alignWithLayer !== false);
      }

      if (effectId === "patternOverlay") {
        addBlendModeRow("blendMode", "Blend Mode", eff.blendMode ?? "normal");
        addSliderRow("opacity", "Opacity", eff.opacity != null ? Math.round(eff.opacity * 100) : 100, 0, 100, "%");
        addSliderRow("scale", "Scale", eff.scale != null ? Math.round(eff.scale * 100) : 100, 1, 1000, "%");
        addCheckboxRow("linkWithLayer", "Link with Layer", eff.linkWithLayer !== false);
      }

      if (effectId === "stroke") {
        addSliderRow("size", "Size", eff.size ?? 3, 0, 250, "px");
        addDropdownRow("position", "Position", eff.position ?? "outside",
          [{ value: "outside", label: "Outside" }, { value: "inside", label: "Inside" }, { value: "center", label: "Center" }]);
        addDropdownRow("fillType", "Fill Type", eff.fillType ?? "color",
          [{ value: "color", label: "Color" }, { value: "gradient", label: "Gradient" }, { value: "pattern", label: "Pattern" }]);
        addColorRow("color", "Color", eff.color ?? { r: 255, g: 0, b: 0, a: 255 });
        addBlendModeRow("blendMode", "Blend Mode", eff.blendMode ?? "normal");
        addSliderRow("opacity", "Opacity", eff.opacity != null ? Math.round(eff.opacity * 100) : 100, 0, 100, "%");
      }

      // ── wire color swatches ─────────────────────────────────────────────
      paramsPanel.querySelectorAll<HTMLButtonElement>("[data-color-swatch]").forEach((btn) => {
        btn.addEventListener("click", async () => {
          const key = btn.dataset.colorSwatch!;
          // btn.style.background may be hex or rgb() depending on browser/jsdom.
          // Try hex first, fall back to parsing rgb().
          const rawBg = btn.style.background;
          let rgb = hexToRgb(rawBg);
          if (!rgb) {
            const m = rawBg.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
            if (m) rgb = { r: parseInt(m[1]!), g: parseInt(m[2]!), b: parseInt(m[3]!) };
          }
          const initial = rgb ?? { r: 0, g: 0, b: 0 };
          const result = await deps.openColorPicker(initial);
          if (!result) return;
          btn.style.background = rgbToHex(result);
          // Ensure effect object exists before writing to it.
          if (!styles[currentEffect]) styles[currentEffect] = { enabled: true };
          setNestedKey(styles[currentEffect], key, rgbToRgba(result, 255));
        });
      });

      // ── wire contour thumbnails ──────────────────────────────────────────
      paramsPanel.querySelectorAll<HTMLButtonElement>("[data-contour-preset]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const name = btn.dataset.contourPreset!;
          const preset = CONTOUR_PRESETS.find((p) => p.name === name);
          if (!preset) return;
          if (!styles[currentEffect]) styles[currentEffect] = { enabled: true };
          styles[currentEffect].glossContour = preset.spec;
          // Update selection highlight
          paramsPanel.querySelectorAll<HTMLButtonElement>("[data-contour-preset]").forEach((b) => {
            const sel = b.dataset.contourPreset === name;
            b.style.background = sel ? "var(--accent)" : "#333";
            b.style.borderColor = sel ? "var(--accent-light,#60b0ff)" : "#555";
          });
        });
      });

      // ── wire sliders ─────────────────────────────────────────────────────
      paramsPanel.querySelectorAll<HTMLInputElement>("input[type='range'][data-param]").forEach((slider) => {
        slider.addEventListener("input", () => {
          const key = slider.dataset.param!;
          const valSpan = paramsPanel.querySelector<HTMLElement>(`[data-value="${key}"]`);
          if (valSpan) valSpan.textContent = slider.value;
          if (!styles[currentEffect]) styles[currentEffect] = { enabled: true };
          writeSliderValue(styles[currentEffect], key, parseFloat(slider.value));
        });
      });

      // ── wire selects ─────────────────────────────────────────────────────
      paramsPanel.querySelectorAll<HTMLSelectElement>("select[data-param]").forEach((sel) => {
        sel.addEventListener("change", () => {
          const key = sel.dataset.param!;
          if (!styles[currentEffect]) styles[currentEffect] = { enabled: true };
          styles[currentEffect][key] = sel.value;
        });
      });

      // ── wire checkboxes ──────────────────────────────────────────────────
      paramsPanel.querySelectorAll<HTMLInputElement>("input[type='checkbox'][data-param]").forEach((cb) => {
        cb.addEventListener("change", () => {
          const key = cb.dataset.param!;
          if (!styles[currentEffect]) styles[currentEffect] = { enabled: true };
          styles[currentEffect][key] = cb.checked;
        });
      });

      // ── wire Use Global Light checkbox ──────────────────────────────────
      const ugl = paramsPanel.querySelector<HTMLInputElement>("[data-global-light]");
      if (ugl) {
        const angleSlider = paramsPanel.querySelector<HTMLInputElement>(`input[type='range'][data-param='angle']`);
        const angleValue = paramsPanel.querySelector<HTMLElement>(`[data-value='angle']`);
        ugl.addEventListener("change", () => {
          if (!styles[currentEffect]) styles[currentEffect] = { enabled: true };
          styles[currentEffect].useGlobalLight = ugl.checked;
          if (ugl.checked) {
            // Snap to global light
            styles[currentEffect].angle = deps.globalLightAngle;
            if (angleSlider) angleSlider.value = String(deps.globalLightAngle);
            if (angleValue) angleValue.textContent = String(deps.globalLightAngle);
          }
          if (angleSlider) angleSlider.disabled = ugl.checked;
        });
        // Disable angle slider if currently using global light
        if (angleSlider && eff.useGlobalLight) angleSlider.disabled = true;
      }
    }

    // ── Row builder helpers ──────────────────────────────────────────────────

    function row(labelText: string, controlHtml: string): void {
      const div = document.createElement("div");
      div.style.cssText = "margin-bottom:10px;";
      div.innerHTML = `
        <label style="display:block;color:#aaa;font-size:11px;margin-bottom:4px;">${labelText}</label>
        <div style="display:flex;align-items:center;gap:8px;">${controlHtml}</div>
      `;
      paramsPanel.appendChild(div);
    }

    function addSliderRow(key: string, label: string, value: number, min: number, max: number, unit = ""): void {
      row(label,
        `<input type="range" data-param="${key}" min="${min}" max="${max}" value="${value}" style="flex:1;">` +
        `<span data-value="${key}" style="color:#fff;font-size:11px;min-width:30px;text-align:right;">${value}</span>` +
        (unit ? `<span style="color:#888;font-size:11px;">${unit}</span>` : "")
      );
    }

    function addBlendModeRow(key: string, label: string, value: string): void {
      const opts = BLEND_MODES.map(
        (m) => `<option value="${m}"${m === value ? " selected" : ""}>${m}</option>`
      ).join("");
      row(label,
        `<select data-param="${key}" style="flex:1;padding:4px;background:#333;border:1px solid #555;color:#fff;border-radius:1px;">${opts}</select>`
      );
    }

    function addDropdownRow(key: string, label: string, value: string, options: Array<{ value: string; label: string }>): void {
      const opts = options.map(
        (o) => `<option value="${o.value}"${o.value === value ? " selected" : ""}>${o.label}</option>`
      ).join("");
      row(label,
        `<select data-param="${key}" style="flex:1;padding:4px;background:#333;border:1px solid #555;color:#fff;border-radius:1px;">${opts}</select>`
      );
    }

    function addColorRow(key: string, label: string, colorVal: RGBA): void {
      const rgb: RGB = rgbaToRgb(colorVal);
      const hex = rgbToHex(rgb);
      const div = document.createElement("div");
      div.style.cssText = "margin-bottom:10px;";
      div.innerHTML = `
        <label style="display:block;color:#aaa;font-size:11px;margin-bottom:4px;">${label}</label>
        <div style="display:flex;align-items:center;gap:8px;">
          <button data-color-swatch="${key}" title="Click to pick color"
            aria-label="Pick color for ${label}"
            style="width:32px;height:20px;background:${hex};border:1px solid #888;border-radius:2px;cursor:pointer;flex-shrink:0;"></button>
          <span style="color:#aaa;font-size:11px;">${hex.toUpperCase()}</span>
        </div>
      `;
      paramsPanel.appendChild(div);
    }

    function addCheckboxRow(key: string, label: string, checked: boolean): void {
      const div = document.createElement("div");
      div.style.cssText = "margin-bottom:10px;display:flex;align-items:center;gap:8px;";
      div.innerHTML = `
        <input type="checkbox" data-param="${key}" ${checked ? "checked" : ""}>
        <label style="color:#aaa;font-size:11px;cursor:pointer;">${label}</label>
      `;
      paramsPanel.appendChild(div);
    }

    function addGlobalLightRow(angleKey: string, angleLabel: string, angleValue: number, useGlobal: boolean): void {
      // Angle slider
      const sliderDiv = document.createElement("div");
      sliderDiv.style.cssText = "margin-bottom:10px;";
      sliderDiv.innerHTML = `
        <label style="display:block;color:#aaa;font-size:11px;margin-bottom:4px;">${angleLabel}</label>
        <div style="display:flex;align-items:center;gap:8px;">
          <input type="range" data-param="${angleKey}" min="0" max="360" value="${angleValue}" style="flex:1;" ${useGlobal ? "disabled" : ""}>
          <span data-value="${angleKey}" style="color:#fff;font-size:11px;min-width:30px;text-align:right;">${angleValue}</span>
          <span style="color:#888;font-size:11px;">°</span>
        </div>
      `;
      paramsPanel.appendChild(sliderDiv);

      // Use Global Light checkbox
      const glDiv = document.createElement("div");
      glDiv.style.cssText = "margin-bottom:10px;display:flex;align-items:center;gap:8px;";
      glDiv.innerHTML = `
        <input type="checkbox" data-global-light data-param-ugl="useGlobalLight" ${useGlobal ? "checked" : ""}>
        <label style="color:#aaa;font-size:11px;cursor:pointer;">Use Global Light</label>
      `;
      paramsPanel.appendChild(glDiv);
    }

    function addSectionLabel(text: string): void {
      const el = document.createElement("div");
      el.style.cssText =
        "color:#888;font-size:10px;text-transform:uppercase;letter-spacing:0.08em;" +
        "border-top:1px solid #444;padding-top:8px;margin:12px 0 8px;";
      el.textContent = text;
      paramsPanel.appendChild(el);
    }

    function addContourRow(key: string, current: ContourSpec | undefined): void {
      const div = document.createElement("div");
      div.style.cssText = "margin-bottom:10px;";
      div.innerHTML = contourThumbnails(current?.presetName);
      paramsPanel.appendChild(div);
    }

    // ── Nested key helpers ───────────────────────────────────────────────────

    /**
     * Write a value using a dotted path like "fill.color" into an effect object.
     * Handles special gradient stop paths like "gradient.stop0.color".
     */
    function setNestedKey(eff: Record<string, any>, path: string, value: unknown): void {
      if (!eff) return;
      const parts = path.split(".");
      if (parts.length === 1) {
        eff[parts[0]!] = value;
        return;
      }
      if (parts[0] === "fill" && parts[1] === "color") {
        if (!eff.fill) eff.fill = { kind: "color", color: value };
        else eff.fill = { ...eff.fill, kind: "color", color: value };
        return;
      }
      if (parts[0] === "gradient" && parts[1] && parts[1].startsWith("stop")) {
        const idx = parseInt(parts[1].replace("stop", ""), 10);
        if (!eff.gradient) eff.gradient = { colorStops: [], opacityStops: [], smoothness: 1 };
        if (!eff.gradient.colorStops) eff.gradient.colorStops = [];
        while (eff.gradient.colorStops.length <= idx) {
          eff.gradient.colorStops.push({ position: idx === 0 ? 0 : 1, color: { r: 0, g: 0, b: 0, a: 255 } });
        }
        eff.gradient.colorStops[idx] = {
          ...eff.gradient.colorStops[idx],
          color: value,
        };
        return;
      }
    }

    /**
     * Write a slider value, normalising percentage-based fields to [0..1].
     * Fields stored as percentages (%): opacity, spread, choke, noise, range, jitter,
     * highlightOpacity, shadowOpacity.
     * Fields stored as [0..1] scaled from UI 1..1000: scale (gradientOverlay / patternOverlay).
     * All others are stored as-is.
     */
    const PERCENT_FIELDS = new Set([
      "opacity", "spread", "choke", "noise", "range", "jitter",
      "highlightOpacity", "shadowOpacity",
    ]);
    const SCALE_FIELDS = new Set(["scale"]);
    const DEPTH_FIELDS = new Set(["depth"]);

    function writeSliderValue(eff: Record<string, any>, key: string, uiVal: number): void {
      if (PERCENT_FIELDS.has(key)) {
        eff[key] = uiVal / 100;
      } else if (SCALE_FIELDS.has(key)) {
        eff[key] = uiVal / 100;
      } else if (DEPTH_FIELDS.has(key) && (currentEffect === "bevelEmboss")) {
        eff[key] = uiVal / 100;
      } else {
        eff[key] = uiVal;
      }
    }

    // ── Initialise ───────────────────────────────────────────────────────────

    showParams("dropShadow");

    // ── Button wiring ────────────────────────────────────────────────────────

    const closeDialog = (result: LayerStyles | null) => {
      overlay.remove();
      // Restore focus to the element that opened the dialog (#213).
      openerEl?.focus?.();
      resolve(result);
    };

    dialog.querySelector('[data-action="close"]')!.addEventListener("click", () => closeDialog(null));
    dialog.querySelector('[data-action="cancel"]')!.addEventListener("click", () => closeDialog(null));
    dialog.querySelector('[data-action="ok"]')!.addEventListener("click", () => closeDialog(styles as LayerStyles));

    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) closeDialog(null);
    });

    // Focus the first focusable element inside the dialog (#213).
    const firstFocusable = dialog.querySelector<HTMLElement>(
      "button, input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    firstFocusable?.focus();

    document.addEventListener("keydown", function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeDialog(null);
        document.removeEventListener("keydown", onKey);
        return;
      }
      // Focus trap: keep Tab / Shift+Tab inside the dialog (#213).
      trapFocus(e, dialog);
    }, { once: false });
  });
}
